import { useContext, useEffect, useMemo, useState } from "react";
import { useBoolean } from "@fluentui/react-hooks"
import { FontIcon, IconButton, Stack, Text, Spinner, TooltipHost, DirectionalHint, Separator } from "@fluentui/react";

import styles from "./Answer.module.css";

import { Citation_Parsed, AISearchRAGCitation_Parsed, isWebSearchCitationParsed, isAISearchRAGCitationParsed } from "../../api/citations";
import { AskResponse, Conversation, historyFeedback } from "../../api";
import { parseAnswer } from "./AnswerParser";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import supersub from 'remark-supersub'
import { CopyButton } from "../common/Button";
import { AppStateContext } from "../../state/AppProvider";

interface Props {
    answer: AskResponse;
    refInfo?:{
        conv: Conversation
        mesId: string
    };
    onCitationClicked: (citedDocument: Citation_Parsed) => void;
    isLoading?: boolean;
}

const tryParseURL = (url_str: string) => {
    try {
        return new URL(url_str)
    }
    catch {
        return null
    }
}

const createCitationFileTitle = (citation: AISearchRAGCitation_Parsed) => {
    if (citation.part) {
        return `${citation.filepath} - Part ${citation.part}`
    }
    return citation.filepath
}

const CitationItem = (props: {citation: Citation_Parsed, index: number, onCitationClicked?: (c: Citation_Parsed) => void}) => {
    const {citation, index, onCitationClicked} = props
    const onClick = onCitationClicked?.bind(null, citation)
    if (isWebSearchCitationParsed(citation)) {
        return (
            <button
                className={styles.webCitationButton}
                onClick={() => window.open(citation.url, "_blank")}
            >
                {index.toString() + ' : ' + citation.title}
            </button>
        )
    }
    else if (isAISearchRAGCitationParsed(citation)) {
        const title = createCitationFileTitle(citation)
        return (
            <span 
                title={title} 
                tabIndex={0} 
                role="link" 
                key={index} 
                onClick={onCitationClicked?.bind(null, citation)} 
                onKeyDown={e => e.key === "Enter" || e.key === " " ? onClick : null}
                className={styles.citationContainer}
                aria-label={title}
            >
                <div className={styles.citation}>{index}</div>
                {title}
            </span>);
    }
    return (<></>)
}

export const Answer = ({
    answer,
    refInfo,
    onCitationClicked,
    isLoading = false
}: Props) => {
    const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] = useBoolean(false);
    const appStateContext = useContext(AppStateContext);

    const parsedAnswer = useMemo(() => parseAnswer(answer), [answer]);
    const [chevronIsExpanded, setChevronIsExpanded] = useState(isRefAccordionOpen);
    const [isFeedbacking, setIsFeedbacking] = useState(false)

    const displayCitation = Boolean(parsedAnswer.citations.length)

    const handleChevronClick = () => {
        setChevronIsExpanded(!chevronIsExpanded);
        toggleIsRefAccordionOpen();
    };

    useEffect(() => {
        setChevronIsExpanded(isRefAccordionOpen);
    }, [isRefAccordionOpen]);

    const convRef = refInfo?.conv
    const mesRef = refInfo && convRef?.messages.find((v) => v.id == refInfo.mesId)
    const likeText = "like"
    const dislikeText = "dislike"
    const handleFeedback = convRef && mesRef && convRef?.rag_ids?.length !== 0 ? async (text: string) => {
        setIsFeedbacking(true)
        const succeeded = await historyFeedback(convRef.id, refInfo.mesId, text)
        // クライアント側のデータ変更
        if(succeeded) {
            appStateContext?.dispatch({type: "SET_MESSAGE_FEEDBACK", payload: {convId: convRef.id, mesId: refInfo.mesId, feedback: text}})
        }
        setIsFeedbacking(false)
    } : undefined

    return (
        <Stack tokens={{childrenGap: "4px"}}>
            <Stack className={styles.answerContainer} tabIndex={0} data-testid="answer-content">
                <Stack tokens={{childrenGap: "11px"}} style={{width: "100%"}}>
                    {/* ローディングアニメーション */}
                    {isLoading && (
                        <Spinner className={styles.spinnerContainer} />
                    )}
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm, supersub]}
                        children={parsedAnswer.markdownFormatText}
                        className={styles.answerText}
                        components={{
                            a: ({ href, children, ...props }) => {
                                // 条件付きでリンクを変換
                                const url = href ? tryParseURL(href) : null
                                let isBlank = false
                                let transformedHref;
                                let assistantFileMessage = answer.assistantFileMessages?.find((fileMessage) => fileMessage.assistantFileID === href);
                                if (assistantFileMessage !== undefined && assistantFileMessage !== null && assistantFileMessage.content !== "") {
                                    transformedHref = `/api/download/assistantsapiCreatFile/${assistantFileMessage.content}/${assistantFileMessage.fileName}`; // サーバールートに変換
                                }
                                else if (url) {
                                    // ReactMarkdownは、未知のプロトコルを自動で"javascript:void(0)へ変換する"
                                    if (!url.protocol.toLowerCase().startsWith("javascript")) {
                                        transformedHref = href
                                        isBlank = true
                                    }
                                    else {
                                        transformedHref = "#"
                                    }
                                }

                                return (
                                    <a href={transformedHref} target={isBlank ? "_blank" : undefined} rel="noopener noreferrer" {...props}>
                                        {children}
                                    </a>
                                );
                            }
                        }}
                    />
                </Stack>
                <Stack.Item className={styles.answerDisclaimerContainer}>
                    <span className={styles.answerDisclaimer}>AIが生成するコンテンツは誤りを含む可能性があります。</span>
                </Stack.Item>
                { displayCitation && 
                    <>
                        <Separator styles={{
                          root: {
                              width: '100%',
                              position: 'relative',
                              '::before': {
                                backgroundColor: '#d6d6d6',
                              },
                            },
                        }}/>
                        <Stack.Item
                            onKeyDown={e => e.key === "Enter" || e.key === " " ? toggleIsRefAccordionOpen() : null}
                        >
                            <Stack horizontal style={{width: "100%"}} horizontalAlign='start' verticalAlign='center' tokens={{childrenGap: "5px"}}>
                                <Text
                                    className={styles.accordionTitle}
                                    onClick={toggleIsRefAccordionOpen}
                                    aria-label="Open references"
                                    tabIndex={0}
                                    role="button"
                                >
                                    <span>{parsedAnswer.citations.length.toString() + "つの参考資料"}</span>
                                </Text>
                                <FontIcon className={styles.accordionIcon}
                                onClick={handleChevronClick} iconName={chevronIsExpanded ? 'ChevronDown' : 'ChevronRight'}
                                />
                            </Stack>
                        </Stack.Item>
                    </>
                }
                {chevronIsExpanded && 
                    <div style={{ marginTop: 8, display: "flex", flexFlow: "wrap column", maxHeight: "150px", gap: "4px", overflowX: "auto" }}>
                        {parsedAnswer.citations.map((v, idx) => <CitationItem key={idx} citation={v} index={idx+1} onCitationClicked={onCitationClicked}/>)}
                    </div>
                }
            </Stack>
            <Stack horizontal style={{ width: "100%" }}>
                <Stack.Item>
                    <CopyButton copyText={parsedAnswer.markdownFormatText} testId="copy-answer-button" />
                </Stack.Item>
                {handleFeedback && refInfo && (
                    <Stack.Item style={{ margin: "0 0 0 auto", width: "64px" }}>
                        {
                            isFeedbacking ? <Spinner style={{ height: "32px" }} /> :
                                <span>
                                    <TooltipHost content="良い回答です"
                                        directionalHint={DirectionalHint.bottomCenter}>
                                        <IconButton iconProps={{ iconName: mesRef?.user_feedback === likeText ? "LikeSolid" : "Like" }} onClick={() => handleFeedback(mesRef?.user_feedback === likeText ? "" : likeText)} data-testid="feedback-like" data-checked={mesRef?.user_feedback === likeText} />
                                    </TooltipHost>
                                    <TooltipHost content="良くない回答です"
                                        directionalHint={DirectionalHint.bottomCenter}>
                                        <IconButton iconProps={{ iconName: mesRef?.user_feedback === dislikeText ? "DislikeSolid" : "Dislike" }} onClick={() => handleFeedback(mesRef?.user_feedback === dislikeText ? "" : dislikeText)} data-testid="feedback-dislike" data-checked={mesRef?.user_feedback === dislikeText} />
                                    </TooltipHost>
                                </span>
                        }
                    </Stack.Item>
                )
                }
            </Stack>
        </Stack>
    );
};
